import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Hls from 'hls.js';
import { ChevronLeft, ChevronRight, CheckCircle2, NotebookPen, X, Bookmark, Trash2, PlayCircle } from 'lucide-react';
import { courseService } from '../../services/course.service';
import { studyService } from '../../services/study.service';
import { useToast } from '../../components/ui/ToastProvider';
import type { LessonDetail, LessonNote, LessonBookmark } from '@edytech/shared/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const formatTime = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

export const LessonPlayer: React.FC = () => {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const lastSavedRef = useRef<number>(0);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [progress, setProgress] = useState<{ status: string; lastPositionSec: number } | null>(null);

  // Notes drawer state (scoped per lesson so navigating lessons never leaks drafts)
  const [notesOpen, setNotesOpen] = useState(false);
  const [draft, setDraft] = useState<{ lessonId: string; text: string; touched: boolean } | null>(null);
  const [createdNote, setCreatedNote] = useState<{ lessonId: string; id: number } | null>(null);

  const { data, isLoading } = useQuery<LessonDetail>({
    queryKey: ['lesson', lessonId],
    queryFn: () => courseService.getLesson(Number(lessonId)),
    enabled: !!lessonId,
  });

  // Private notes for this lesson (most recent first)
  const { data: notes } = useQuery<LessonNote[]>({
    queryKey: ['lesson-notes', lessonId],
    queryFn: () => studyService.getLessonNotes(Number(lessonId)),
    enabled: !!lessonId,
  });

  // Bookmarks for this lesson
  const { data: bookmarks } = useQuery<LessonBookmark[]>({
    queryKey: ['bookmarks', lessonId],
    queryFn: () => studyService.getBookmarks(Number(lessonId)),
    enabled: !!lessonId,
  });

  const serverNote = notes?.[0] ?? null;
  const createdNoteId = createdNote && createdNote.lessonId === lessonId ? createdNote.id : null;
  const draftForLesson = draft && draft.lessonId === lessonId ? draft : null;

  // Until the user starts typing, the editor is fed directly from the server note.
  const activeNoteId = createdNoteId ?? serverNote?.id ?? null;
  const noteValue = draftForLesson?.touched ? draftForLesson.text : (serverNote?.content ?? '');

  const lesson = data?.lesson;

  // Save progress (throttled to avoid spamming the API)
  const saveProgress = useCallback(async (status: 'IN_PROGRESS' | 'COMPLETED', position: number) => {
    if (!lessonId) return;
    const now = Date.now();
    if (now - lastSavedRef.current < 5000 && status !== 'COMPLETED') return;
    lastSavedRef.current = now;
    try {
      await courseService.saveProgress(Number(lessonId), { status, positionSec: Math.floor(position) });
      queryClient.invalidateQueries({ queryKey: ['lesson', lessonId] });
      queryClient.invalidateQueries({ queryKey: ['course', slug] });
      queryClient.invalidateQueries({ queryKey: ['my-courses'] });
    } catch {
      // Silent — progress is best-effort
    }
  }, [lessonId, queryClient, slug]);

  // Initialize video (HLS or MP4) and resume position
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !lesson) return;

    if (data?.progress?.lastPositionSec) {
      video.currentTime = data.progress.lastPositionSec;
    }

    if (lesson.videoKey) {
      const src = `${API_BASE}/lessons/${lesson.id}/stream`;
      if (Hls.isSupported()) {
        hlsRef.current = new Hls({ enableWorker: true });
        hlsRef.current.loadSource(src);
        hlsRef.current.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
      }
    } else if (lesson.vimeoUrl) {
      video.src = lesson.vimeoUrl;
    }

    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      hlsRef.current = null;
    };
  }, [lesson, data?.progress?.lastPositionSec]);

  // Track time + save progress periodically
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !lesson) return;

    const onTimeUpdate = () => {
      if (video.currentTime && !video.paused && !video.ended) {
        setProgress({ status: 'IN_PROGRESS', lastPositionSec: video.currentTime });
        saveProgress('IN_PROGRESS', video.currentTime);
      }
    };

    const onEnded = () => {
      saveProgress('COMPLETED', video.duration || 0);
      setProgress({ status: 'COMPLETED', lastPositionSec: video.duration || 0 });
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, [lesson, saveProgress]);

  // Save final position on unmount
  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video?.currentTime) {
        saveProgress('IN_PROGRESS', video.currentTime);
      }
    };
  }, [saveProgress]);

  // Create on first save, update the existing note afterwards. If the server note is not
  // loaded yet when the draft starts, a new note is created (never clobbering).
  const autosaveNote = useCallback(async (content: string) => {
    if (!lessonId || !content.trim()) return;
    try {
      if (activeNoteId) {
        await studyService.updateNote(activeNoteId, { content });
      } else {
        const created = await studyService.createNote({ lessonId: Number(lessonId), content });
        setCreatedNote({ lessonId, id: created.id });
      }
      queryClient.invalidateQueries({ queryKey: ['lesson-notes', lessonId] });
    } catch {
      // Best-effort autosave — keep the draft in the textarea, retry on next keystroke
    }
  }, [lessonId, activeNoteId, queryClient]);

  // Debounced autosave: while the user has an unsaved draft, save after 800ms of inactivity.
  useEffect(() => {
    if (!draftForLesson?.touched) return;
    const timer = setTimeout(() => {
      void autosaveNote(draftForLesson.text);
    }, 800);
    return () => clearTimeout(timer);
  }, [draftForLesson, autosaveNote]);

  const handleNoteChange = (value: string) => {
    if (!lessonId) return;
    setDraft({ lessonId, text: value, touched: true });
  };

  const handleDeleteNote = async () => {
    if (!activeNoteId) return;
    try {
      await studyService.deleteNote(activeNoteId);
      setCreatedNote(null);
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ['lesson-notes', lessonId] });
      showToast('Note deleted', 'info');
    } catch {
      showToast('Failed to delete note', 'error');
    }
  };

  const handleBookmark = async () => {
    if (!lessonId) return;
    const position = Math.floor(videoRef.current?.currentTime ?? 0);
    try {
      await studyService.createBookmark({ lessonId: Number(lessonId), positionSec: position });
      queryClient.invalidateQueries({ queryKey: ['bookmarks', lessonId] });
      showToast(`Bookmarked at ${formatTime(position)}`, 'success');
    } catch {
      showToast('Failed to save bookmark', 'error');
    }
  };

  const jumpToBookmark = (positionSec: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = positionSec;
    showToast(`Jumped to ${formatTime(positionSec)}`, 'info');
  };

  const handleDeleteBookmark = async (id: number) => {
    try {
      await studyService.deleteBookmark(id);
      queryClient.invalidateQueries({ queryKey: ['bookmarks', lessonId] });
    } catch {
      showToast('Failed to delete bookmark', 'error');
    }
  };

  if (isLoading) {
    return <div className="py-20 text-center text-[rgb(var(--light-secondary))]">Loading lesson...</div>;
  }

  if (!lesson) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold">Lesson not found</h1>
        <Link to={`/courses/${slug}`} className="mt-4 inline-block font-medium text-primary hover:underline">Back to course</Link>
      </div>
    );
  }

  const isComplete = progress?.status === 'COMPLETED' || data?.progress?.status === 'COMPLETED';

  return (
    <div className="min-h-screen bg-black">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link to={`/courses/${slug}`} className="mb-4 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to course
        </Link>

        <div className="relative">
          <div className="overflow-hidden rounded-xl">
            <video
              ref={videoRef}
              controls
              playsInline
              className="aspect-video w-full bg-black"
              controlsList="nodownload"
            />
          </div>
          <button
            onClick={() => setNotesOpen(true)}
            className="absolute right-3 top-3 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20"
          >
            <NotebookPen className="h-4 w-4" /> Notes
          </button>
        </div>

        <div className="mt-6">
          <h1 className="text-2xl font-bold text-white">{lesson.title}</h1>
          {isComplete && (
            <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-green-400">
              <CheckCircle2 className="h-4 w-4" /> Completed
            </span>
          )}
          {lesson.description && (
            <p className="mt-3 text-white/70">{lesson.description}</p>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between gap-4">
          {data?.navigation?.prev ? (
            <Link
              to={`/learn/${slug}/lessons/${data.navigation.prev.id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
            >
              <ChevronLeft className="h-4 w-4" /> {data.navigation.prev.title}
            </Link>
          ) : <span />}
          {data?.navigation?.next ? (
            <Link
              to={`/learn/${slug}/lessons/${data.navigation.next.id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
            >
              {data.navigation.next.title} <ChevronRight className="h-4 w-4" />
            </Link>
          ) : <span />}
        </div>
      </div>

      {/* Notes & Bookmarks drawer (right-hand, collapsible) */}
      {notesOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setNotesOpen(false)} />
          <aside className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col border-l border-white/10 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h2 className="text-sm font-semibold text-white">Notes &amp; Bookmarks</h2>
              <button
                onClick={() => setNotesOpen(false)}
                className="text-white/60 transition-colors hover:text-white"
                aria-label="Close notes"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <textarea
                value={noteValue}
                onChange={(e) => handleNoteChange(e.target.value)}
                placeholder="Write your private notes for this lesson… (auto-saved)"
                rows={10}
                className="w-full rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-primary focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-white/40">Auto-saved as you type</p>
                {activeNoteId && (
                  <button
                    onClick={handleDeleteNote}
                    className="inline-flex items-center gap-1 text-xs text-white/40 transition-colors hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete note
                  </button>
                )}
              </div>

              <div className="mt-6 border-t border-white/10 pt-4">
                <h3 className="text-sm font-semibold text-white">Bookmarks</h3>
                <button
                  onClick={handleBookmark}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20"
                >
                  <Bookmark className="h-4 w-4" /> Bookmark at {formatTime(progress?.lastPositionSec ?? 0)}
                </button>

                <ul className="mt-3 space-y-2">
                  {!bookmarks?.length && (
                    <li className="text-sm text-white/40">No bookmarks yet — save a spot in the video.</li>
                  )}
                  {bookmarks?.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{formatTime(b.positionSec)}</p>
                        {b.note && <p className="mt-0.5 text-xs text-white/50">{b.note}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => jumpToBookmark(b.positionSec)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <PlayCircle className="h-4 w-4" /> Jump
                        </button>
                        <button
                          onClick={() => handleDeleteBookmark(b.id)}
                          className="text-white/40 transition-colors hover:text-red-400"
                          aria-label="Delete bookmark"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};


